// components/PetTrainingExperience.tsx

import React from "react";
import {
    ArrowRight,
    Brain,
    Play,
    Trophy,
    Volume2,
} from "lucide-react";
import CommonButton from "../CommonButton";

type TrainingCard = {
    title: string;
    desc: string;
    icon: React.ReactNode;
};

const trainingCards: TrainingCard[] = [
    {
        title: "Puppy Intelligence",
        desc: "Confidence & social training.",
        icon: <Brain size={18} />,
    },
    {
        title: "Competition Level",
        desc: "Advanced obedience mastery.",
        icon: <Trophy size={18} />,
    },
];

const stats: [string, string][] = [
    ["12K+", "Pets Trained"],
    ["98%", "Success Rate"],
    ["30 Days", "Avg Program"],
];

interface PetTrainingExperienceProps {
    handlePetTrainingClick?: () => void;
}

const PetTrainingExperience: React.FC<PetTrainingExperienceProps> = ({
    handlePetTrainingClick,
}) => {
    return (
        <section className="w-full overflow-hidden bg-white relative rounded-3xl border border-gray-200">
            <div className="relative max-w-7xl mx-auto p-4 sm:p-6 lg:p-8">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 items-stretch">

                    {/* LEFT IMAGE */}
                    <div className="relative min-h-[420px] sm:min-h-[500px] lg:min-h-[600px] rounded-[2rem] sm:rounded-[3rem] overflow-hidden">
                        <img
                            src="https://images.unsplash.com/photo-1517849845537-4d257902454a?w=1200&q=80"
                            className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 hover:scale-105"
                            alt="Pet Training"
                        />

                        {/* TOP FLOAT */}
                        <div className="absolute top-4 left-4 sm:top-6 sm:left-6 bg-softCream/90 backdrop-blur-xl border border-accent/30 rounded-2xl px-3 sm:px-4 py-2.5 sm:py-3 flex items-center gap-2 sm:gap-3">
                            <Volume2
                                className="text-mainBrandColor shrink-0"
                                size={18}
                            />

                            <div>
                                <p className="text-logoHeading text-[10px] sm:text-xs font-black uppercase tracking-widest">
                                    Live Training
                                </p>

                                <p className="text-logoHeading/60 text-[9px] sm:text-[10px]">
                                    Stay • Heel • Recall
                                </p>
                            </div>
                        </div>

                        {/* PLAY */}
                        <button
                            type="button"
                            className="absolute top-4 right-4 sm:top-6 sm:right-6 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-softCream/80 backdrop-blur-xl border border-accent/30 flex items-center justify-center hover:scale-110 transition-all"
                        >
                            <Play
                                className="text-logoHeading ml-0.5 sm:ml-1"
                                size={18}
                                fill="#2F5E2E"
                            />
                        </button>

                        {/* BOTTOM CONTENT */}
                        <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-8 lg:p-10">
                            <p className="text-accent uppercase tracking-[0.2em] sm:tracking-[0.3em] text-[10px] sm:text-xs font-black mb-3 sm:mb-4">
                                Elite Academy
                            </p>

                            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white leading-[0.95]">
                                Smart Training
                                <br />
                                For Modern Pets
                            </h2>

                            <div className="mt-6 sm:mt-8">
                                <CommonButton
                                    icon={<ArrowRight size={18} />}
                                    text="Start Training"
                                    variant="filled"
                                    rounded="md"
                                    iconTextClassName="px-3 py-2"
                                    onClick={handlePetTrainingClick}
                                />
                            </div>
                        </div>
                    </div>

                    {/* RIGHT SIDE */}
                    <div className="flex flex-col gap-6 lg:gap-8">

                        {/* TEXT */}
                        <div>
                            <span className="inline-block bg-headerGreen-light/40 border border-accent/30 backdrop-blur-xl text-logoHeading px-4 sm:px-5 py-2 rounded-full text-[9px] sm:text-[10px] uppercase tracking-[0.2em] sm:tracking-[0.3em] font-black">
                                Dubai Pet Training
                            </span>

                            <h3 className="text-3xl sm:text-4xl lg:text-5xl font-black text-logoHeading leading-[0.9] mt-6 sm:mt-8">
                                Discipline Meets Luxury
                            </h3>

                            <p className="text-logoHeading/70 mt-5 sm:mt-6 max-w-lg leading-relaxed text-sm sm:text-base">
                                Advanced behavioral coaching designed for luxury
                                apartments, villas and high-energy pets living
                                in Dubai.
                            </p>
                        </div>

                        {/* CARDS */}
                        <div className="space-y-4 sm:space-y-5">
                            {trainingCards.map((item, index) => (
                                <div
                                    key={index}
                                    className="group relative overflow-hidden rounded-2xl sm:rounded-[2rem] bg-softCream border border-accent/20 backdrop-blur-xl p-4 sm:p-5 flex items-center justify-between hover:bg-headerGreen-light/30 transition-all shadow-sm"
                                >
                                    <div className="flex items-center gap-3 sm:gap-5 min-w-0">
                                        <div className="w-10 h-10 sm:w-12 sm:h-12 shrink-0 rounded-xl sm:rounded-2xl bg-mainBrandColor text-white flex items-center justify-center shadow-2xl">
                                            {item.icon}
                                        </div>

                                        <div className="min-w-0">
                                            <h4 className="text-logoHeading text-base sm:text-lg font-black truncate">
                                                {item.title}
                                            </h4>

                                            <p className="text-logoHeading/60 text-xs sm:text-sm mt-1">
                                                {item.desc}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* STATS */}
                        <div className="grid grid-cols-3 gap-2 sm:gap-4">
                            {stats.map(([value, label], index) => (
                                <div
                                    key={index}
                                    className="rounded-2xl sm:rounded-[2rem] bg-softCream border border-accent/20 backdrop-blur-xl p-3 sm:p-4 lg:p-6 text-center shadow-sm"
                                >
                                    <p className="text-mainBrandColor text-base sm:text-xl lg:text-2xl font-black">
                                        {value}
                                    </p>

                                    <p className="text-logoHeading/60 text-[7px] sm:text-[9px] lg:text-xs uppercase tracking-wider sm:tracking-widest mt-1.5 sm:mt-2">
                                        {label}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default PetTrainingExperience;